import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { getPublicPlans, getUsage, getUsageSummary } from './api'
import {
  cancelSubscription,
  changePlan,
  getBillingOverview,
  getInvoice,
  listInvoices,
  orderCreditPack,
  subscribe,
  updateBillingProfile,
} from './billingApi'
import type { UsageSummary, UsageWindowOption } from './types'

export function usageSummaryQueryOptions(tenantId: string) {
  return queryOptions({
    queryKey: ['billing', 'usage-summary', tenantId] as const,
    queryFn: () => getUsageSummary(tenantId),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}

/** Fonte unica da faixa global, do indicador do topbar, do card do Dashboard e dos selos de pausa. */
export function useUsageSummary(tenantId: string | undefined) {
  return useQuery({
    ...usageSummaryQueryOptions(tenantId ?? ''),
    enabled: tenantId !== undefined,
  })
}

export function useUsage(tenantId: string | undefined, days: UsageWindowOption) {
  return useQuery({
    queryKey: ['billing', 'usage', tenantId ?? '', days] as const,
    queryFn: () => getUsage(tenantId ?? '', days),
    staleTime: 30_000,
    enabled: tenantId !== undefined,
  })
}

export function usePublicPlans() {
  return useQuery({
    queryKey: ['billing', 'public-plans'] as const,
    queryFn: getPublicPlans,
    staleTime: 5 * 60_000,
  })
}

/** "Agente pausado" so existe em Enforce (o servidor ja deriva assim); a UI nunca decide o portao. */
export function isPausedForCredits(summary: UsageSummary | undefined): boolean {
  return summary?.pausedForCredits === true
}

export function useBillingOverview(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['billing', 'overview', tenantId ?? ''] as const,
    queryFn: () => getBillingOverview(tenantId ?? ''),
    staleTime: 30_000,
    enabled: tenantId !== undefined,
  })
}

export function useInvoices(tenantId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['billing', 'invoices', tenantId ?? ''] as const,
    queryFn: () => listInvoices(tenantId ?? ''),
    staleTime: 30_000,
    enabled: tenantId !== undefined && enabled,
  })
}

export function useInvoice(tenantId: string | undefined, invoiceId: string | undefined) {
  return useQuery({
    queryKey: ['billing', 'invoice', tenantId ?? '', invoiceId ?? ''] as const,
    queryFn: () => getInvoice(tenantId ?? '', invoiceId ?? ''),
    staleTime: 60_000,
    enabled: tenantId !== undefined && invoiceId !== undefined,
  })
}

/** Toda escrita de cobrança invalida `['billing', ...]` (estado, faixa, topbar) e `['analytics', ...]`. */
function useBillingInvalidation() {
  const queryClient = useQueryClient()
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['billing'] }),
      queryClient.invalidateQueries({ queryKey: ['analytics'] }),
    ])
  }
}

export function useUpdateBillingProfile(tenantId: string) {
  const invalidate = useBillingInvalidation()
  return useMutation({
    mutationFn: (body: { legalName: string; taxId: string; email: string }) =>
      updateBillingProfile(tenantId, body),
    onSuccess: invalidate,
  })
}

export function useSubscribe(tenantId: string) {
  const invalidate = useBillingInvalidation()
  return useMutation({
    mutationFn: (planCode: string) => subscribe(tenantId, planCode),
    onSuccess: invalidate,
  })
}

export function useCancelSubscription(tenantId: string) {
  const invalidate = useBillingInvalidation()
  return useMutation({
    mutationFn: () => cancelSubscription(tenantId),
    onSuccess: invalidate,
  })
}

export function useChangePlan(tenantId: string) {
  const invalidate = useBillingInvalidation()
  return useMutation({
    mutationFn: (planCode: string) => changePlan(tenantId, planCode),
    onSuccess: invalidate,
  })
}

export function useOrderCreditPack(tenantId: string) {
  const invalidate = useBillingInvalidation()
  return useMutation({
    mutationFn: (packCode: string) => orderCreditPack(tenantId, packCode),
    onSuccess: invalidate,
  })
}
